import { Download } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { isAxiosError } from "axios";
import { useEffect, useState } from "react";

import Search from "../../../components/Search";
import {
  create,
  deleteCenter,
  exportAll,
  update,
  useList,
  isInUse,
} from "../../../services/carrier";
import { getLocal, getToken } from "../../../storage/auth";
import CustomRegimeForm from "./components/Form";
import DataTable from "./components/DataTable";
import { hasClaim } from "../../../storage/userClaims";
import { CarrierProps } from "../../../types/carrier";
import { useSnackbar } from "notistack";

interface IParams {
  page: number;
  keyword: string;
  sort: string;
  limit: number;
  uri: string;
  param: string;
}

export default function ShipmentInstructions() {
  const { enqueueSnackbar } = useSnackbar();
  const [shipment, setShipment] = useState<CarrierProps>({
    ATIVO: true,
  } as CarrierProps);

  const [shipmentId, setShipmentId] = useState<number>();
  const [isSaving, setIsSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [userValidation, setUserValidation] = useState<string>();
  const [token, setToken] = useState<string>();
  const [readOnly, setReadOnly] = useState<boolean>(false);
  const [controlEdit, setControlEdit] = useState({
    loading: false,
    isInUse: false,
  });
  const getTokenSession = () => {
    const getTokens = (getToken()) as any;
    const getUser = (getLocal("userInfo")) as any;
    setToken(getTokens);
    setUserValidation(JSON.parse(getUser));
  };

  useEffect(() => {
    getTokenSession();
  }, []);

  const [params, setParams] = useState<IParams>({
    page: 1,
    limit: 10,
  } as IParams);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [errors, setErrors] = useState<string[]>();
  const { data, meta, isLoading } = useList(params, token);

  const handleRequest = (requestParams: any) => {
    setParams((prevState) => ({ ...prevState, ...requestParams }));
  };

  const handleEditShipment = async (id: string | number) => {
    setErrors([])
    setReadOnly(false);
    const editCustomRegime = data.filter(
      (item: CarrierProps) => item.ID_TRANSPORTADOR === id
    );

    if (editCustomRegime.length) {
      const data = editCustomRegime[0];
      setShipment({
        ...data,
      });
      setModalOpen(true);
    }
    try {
      setControlEdit({
        loading: true,
        isInUse: true,
      });
      const { inUse } = await isInUse(id);
      setControlEdit({
        loading: false,
        isInUse: inUse,
      });
    } catch (error) {
      enqueueSnackbar("It's not posible to edit, try again later", {
        variant: "error",
      });
      setControlEdit({
        loading: false,
        isInUse: true,
      });
    }
  };

  const handleDeleteCustomRegimeConfirmation = (id: number) => {
    setShipmentId(id);
    setOpenDeleteModal(true);
  };

  const handleDeleteCustomRegime = async () => {
    if (shipmentId) {
      try {
        setIsSaving(true);
        await deleteCenter(shipmentId);
        enqueueSnackbar("Carrier deleted successful", {
          variant: "success",
        });
        setParams({ ...params });
      } catch (err: any) {
        if (err.response.status) {
          enqueueSnackbar("Carrier is being used", { variant: "error" });
        } else {
          enqueueSnackbar(err.response.data.message[0], { variant: "error" });
        }
      }
      setIsSaving(false);
      setOpenDeleteModal(false);
    }
  };

  const handleFormSubmit = async (newShipment: CarrierProps) => {
    try {
      setIsSaving(true);
      if (newShipment.ID_TRANSPORTADOR) {
        await update(newShipment.ID_TRANSPORTADOR, newShipment);
        enqueueSnackbar("Carrier updated successful", {
          variant: "success",
        });
      } else {
        await create(newShipment);
        enqueueSnackbar("Carrier created successful", {
          variant: "success",
        });
      }
      setShipment({ ATIVO: true } as CarrierProps);
      setParams({ ...params });
      setModalOpen(false);
      setIsSaving(false);
    } catch (err: unknown) {
      if (isAxiosError(err)) {
        setErrors([err.response?.data?.message]);
      }
      setIsSaving(false);
    }
    setIsSaving(false);
  };

  const exportCustomsRegimes = async () => {
    try {
      await exportAll(params);
    } catch (err) {}
  };

  const handleCancelEditCustomRegime = () => {
    setShipment({} as CarrierProps);
    setErrors([]);
    setModalOpen(false);
    setControlEdit({
      loading: false,
      isInUse: false,
    });
  };
  const onViewCustomRegime = (id: string | number) => {
    setErrors([]);
    setReadOnly(true);

    const editCustomRegime = data.filter(
      (item: CarrierProps) => item.ID_TRANSPORTADOR === id
    );
    if (editCustomRegime.length) {
      const data = editCustomRegime[0];
      setShipment({
        ...data,
      });
    }
    setModalOpen(true);
  };

  const transportationModeData = [
    {
      value: "AIR",
      label: "AIR",
    },
    {
      value: "ROAD",
      label: "ROAD",
    },
    {
      value: "OCEAN",
      label: "OCEAN",
    },
  ];

  return (
    <Grid marginTop={4}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={10}>
          <Typography variant="h5">Carrier</Typography>
        </Grid>
      </Grid>
      <Grid container spacing={2} alignItems="center" marginTop={1}>
        <Grid item xs={2}>
          {!modalOpen ? (
            <Grid item xs={2}>
              <Button
                sx={{
                  display: hasClaim("Carrier - Maintenance") ? "block" : "none",
                }}
                variant="contained"
                onClick={() => {
                  setControlEdit({
                    loading: false,
                    isInUse: false,
                  });
                  setErrors([])
                  setReadOnly(false);
                  setModalOpen(true);
                }}
                fullWidth
                data-testid="new-button"
              >
                New
              </Button>
            </Grid>
          ) : null}
        </Grid>
      </Grid>
      {modalOpen ? (
        <Grid container alignItems="center">
          <CustomRegimeForm
            onSubmit={handleFormSubmit}
            carrier={shipment}
            onCancel={handleCancelEditCustomRegime}
            isLoading={isSaving}
            controlEdit={controlEdit}
            errors={errors}
            readOnly={readOnly}
          />
        </Grid>
      ) : null}
      <Search
        transportationMode={transportationModeData}
        onChange={handleRequest}
      />
      <Grid
        container
        marginTop={2}
        marginBottom={2}
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
      >
        <Grid item>
          <Button
            variant="text"
            onClick={exportCustomsRegimes}
            startIcon={<Download />}
          >
            Export XLSX
          </Button>
        </Grid>
      </Grid>
      <Grid>
        <DataTable
          loading={isLoading}
          data={data}
          center={[]}
          userValidation={userValidation}
          onDelete={handleDeleteCustomRegimeConfirmation}
          onEdit={handleEditShipment}
          onView={onViewCustomRegime}
          rowCount={meta.total || 0}
          onPaginationModelChange={(e: any) => {
            setPaginationModel(e);
            handleRequest({ page: e.page + 1, limit: e.pageSize });
          }}
          paginationModel={paginationModel}
          pagination
          initialState={{
            pagination: { paginationModel: { pageSize: params.limit } },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationMode="server"
        />
      </Grid>

      <Dialog
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Delete Carrier</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure that you want to delete this Carrier?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteModal(false)}  data-testid="cancel-button">Cancel</Button>
          <Button onClick={handleDeleteCustomRegime} data-testid="delete-modal-confirm">Confirm</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
